import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { FiltersRoutingParams } from "app/pages/sales-intelligence/modules/leads-search/types/routing";
import type { InitialLeadsSeed } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/initialSeed";
import type { SiAdvancedSearchFiltersParams } from "app/pages/sales-intelligence/services/navigation/types";

import { toInitialFilterValues } from "../utils/urlSeededFilterValues";

/**
 * Reads the advanced-search deep-link URL params and resolves them to seed filter values.
 * Only this page registers the `filtersPayload` / `industry` / `siteTags` route params, so seeding is
 * page-local by design.
 */
export const useUrlSeededFilterValues = (): InitialLeadsSeed => {
    const { params } = useCurrentStateAndParams<SiAdvancedSearchFiltersParams & FiltersRoutingParams>();

    // Keyed on the *serialized* payload, not the decoded object. `useApplyInitialFilters` applies each seed
    // once by identity, so that clearing all filters lands on the defaults instead of replaying the deep
    // link; the router hands back a freshly decoded object per transition, which would defeat that and make
    // "clear all" restore the link. Serializing restores the identity stability the string params had.
    const serializedPayload = JSON.stringify(params.filtersPayload ?? null);

    return useMemo(() => toInitialFilterValues(params), [serializedPayload, params.industry, params.siteTags]);
};
